import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router';
import {
  Container,
  Typography,
  Tabs,
  Tab,
  Button,
  Alert,
  Box,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import RequestHistoryTable from '../components/RequestHistoryTable';
import RequestForm from '../components/RequestForm';
import ThemeToggle from '../components/ThemeToggle';
import TimeDisplay from '../components/TimeDisplay';
import { requesterService } from '../services/requesterService';
import { Request } from '../types/request';
import { useAuth } from '../contexts/AuthContext';

const RequesterDashboard: React.FC = () => {
  const navigate = useNavigate();
  const auth = useAuth();
  const [tab, setTab] = React.useState(0);
  const [requests, setRequests] = useState<Request[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const refreshRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await requesterService.getRequests();
      setRequests(data);
    } catch (err) {
      setError('Failed to fetch requests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshRequests();
  }, [refreshRequests]);

  const handleLogout = () => {
    auth.logout();
    navigate('/');
  };

  return (
    <Container maxWidth="xl" sx={{ pt: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Document Processing Dashboard</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TimeDisplay />
          <ThemeToggle sx={{ mr: 1 }} />
          <Button 
            variant="outlined"
            onClick={handleLogout}
          >
            Logout
          </Button>
        </Box>
      </Box>
      <Button
        startIcon={<RefreshIcon />}
        onClick={refreshRequests}
        disabled={loading}
        variant="contained"
        sx={{ mb: 3 }}
      >
        Refresh
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} sx={{ mb: 3 }}>
        <Tab label="Submit Request" />
        <Tab label="Request History" />
      </Tabs>

      {tab === 0 && <RequestForm onSuccess={refreshRequests} setSuccess={setSuccess} setError={setError} />}
      {tab === 1 && <RequestHistoryTable requests={requests} loading={loading} />}
    </Container>
  );
};

export default RequesterDashboard;
